import { useLoaderData, useRouteLoaderData } from "react-router-dom";
import { Organization } from "../../../services/organization/organizationModel";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import {
  PREMIUM_SPONSORSHIP_STATUS_LABEL_KEYS,
  PREMIUM_SPONSORSHIP_STATUSES,
  PremiumSponsorship,
  PremiumSponsorshipStatus,
} from "../../../services/premiumSponsorship/premiumSponsorshipModel";
import InvitePremiumSponsorshipsDialog from "./InvitePremiumSponsorshipsDialog";
import PremiumSponsorshipsTable from "./PremiumSponsorshipsTable";
import PremiumSponsorshipsOverview from "./PremiumSponsorshipsOverview";
import { useSearch, useTableSort } from "../../../react-helpers/array";

const PremiumSponsorshipPage = () => {
  const { t } = useTranslation(["organization"]);
  const { organization } = useRouteLoaderData("organization") as {
    organization: Organization;
  };
  const { sponsorships } = useLoaderData() as {
    sponsorships: PremiumSponsorship[];
  };
  const [isInviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [searchedSponsorships, setSearch] = useSearch(
    sponsorships,
    (sponsorship) => sponsorship.email,
  );

  const [statusFilter, setStatusFilter] = useState<
    PremiumSponsorshipStatus | "all"
  >("all");

  const filteredSponsorships = useMemo(
    () =>
      statusFilter === "all"
        ? searchedSponsorships
        : searchedSponsorships.filter(
            (sponsorship) => sponsorship.status === statusFilter,
          ),
    [searchedSponsorships, statusFilter],
  );

  const comparators = useMemo(
    () => ({
      // Sorted on the lifecycle order rather than on the translated label.
      status: (a: PremiumSponsorship, b: PremiumSponsorship) =>
        PREMIUM_SPONSORSHIP_STATUSES.indexOf(a.status) -
        PREMIUM_SPONSORSHIP_STATUSES.indexOf(b.status),
    }),
    [],
  );

  const [sortedSponsorships, sort, toggleSort] = useTableSort(
    filteredSponsorships,
    comparators,
  );

  return (
    <div className="layout_content --bg--organization-dashboard">
      <div className="ui-page sp-page">
        <header className="ui-ph">
          <div className="ui-ph_t">
            <h1>{t("organization:premium-sponsorship.PAGE_TITLE")}</h1>
            <p>{t("organization:premium-sponsorship.SUBTITLE")}</p>
          </div>
        </header>

        <PremiumSponsorshipsOverview
          organization={organization}
          sponsorships={sponsorships}
          onInvite={() => setInviteDialogOpen(true)}
        />

        <div className="sp-toolbar">
          <label className="sp-search">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="text"
              className="input"
              placeholder={t(
                "organization:premium-sponsorship.SEARCH_SPONSORSHIPS",
              )}
              aria-label={t(
                "organization:premium-sponsorship.SEARCH_SPONSORSHIPS",
              )}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <select
            className="select sp-filter"
            aria-label={t("organization:premium-sponsorship.FILTER_BY_STATUS")}
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                e.target.value as PremiumSponsorshipStatus | "all",
              )
            }
          >
            <option value="all">
              {`${t("organization:premium-sponsorship.ALL_STATUSES")} (${sponsorships.length})`}
            </option>
            {PREMIUM_SPONSORSHIP_STATUSES.map((status) => (
              <option key={`status-${status}`} value={status}>
                {`${t(PREMIUM_SPONSORSHIP_STATUS_LABEL_KEYS[status])} (${
                  sponsorships.filter((s) => s.status === status).length
                })`}
              </option>
            ))}
          </select>
        </div>

        <PremiumSponsorshipsTable
          organization={organization}
          sponsorships={sortedSponsorships}
          sort={sort}
          onSort={toggleSort}
        />
      </div>

      {isInviteDialogOpen && (
        <InvitePremiumSponsorshipsDialog
          onClose={() => setInviteDialogOpen(false)}
          organization={organization}
          sponsorships={sponsorships}
        />
      )}
    </div>
  );
};

export default PremiumSponsorshipPage;
